import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Star, Send, Loader2, User, Check, Copy, ExternalLink, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ value, onChange, size = "w-5 h-5", interactive = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange?.(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={interactive ? "cursor-pointer" : "cursor-default"}
          data-testid={interactive ? `star-input-${star}` : undefined}
        >
          <Star
            className={`${size} transition-colors ${
              star <= (hover || value)
                ? 'fill-[#9A7B4F] text-[#9A7B4F]'
                : 'text-[#282828]/12'
            }`}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const date = new Date(review.created_at).toLocaleDateString("en-AU", { month: "short", day: "numeric", year: "numeric" });
  return (
    <div className="py-4 border-b border-[#282828]/5 last:border-0" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0">
            <User className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[13px] text-[#282828] font-medium">{review.customer_name}</p>
            <p className="text-[10px] text-[#676664]/40">{date}</p>
          </div>
        </div>
        <StarRating value={review.rating} size="w-3 h-3" />
      </div>
      {review.review_text && (
        <p className="text-[13px] text-[#5A5A5A] leading-relaxed mt-2.5 pl-[42px]">{review.review_text}</p>
      )}
    </div>
  );
}

export default function ReviewSection({ outfitId, reviews, onRefresh, hideHeader = false, externalShowForm, onShowFormChange }) {
  const [internalShowForm, setInternalShowForm] = useState(false);
  const showForm = externalShowForm !== undefined ? externalShowForm : internalShowForm;
  const setShowForm = (v) => {
    if (onShowFormChange) onShowFormChange(typeof v === "function" ? v(showForm) : v);
    else setInternalShowForm(v);
  };
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  // Batch 9 — post-submit Google share modal
  const [thankYou, setThankYou] = useState(null);  // { mode: "high"|"low", rating, text, googleUrl }
  const [googleUrl, setGoogleUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/review-requests/google-link`)
      .then(r => { if (!cancelled) setGoogleUrl(r.data?.google_write_review_url || ""); })
      .catch(() => { if (!cancelled) setGoogleUrl(""); });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (rating === 0) { toast.error("Please select a rating"); return; }

    setSubmitting(true);
    try {
      const reviewText = text.trim();
      await axios.post(`${API}/outfits/${outfitId}/reviews`, {
        customer_name: name.trim(),
        customer_email: email.trim(),
        rating,
        review_text: reviewText,
      });
      setSubmitted(true);
      setShowForm(false);
      toast.success("Review submitted for approval");
      onRefresh?.();
      // Open the Google-share / private-feedback modal
      setThankYou({
        mode: rating >= 4 ? "high" : "low",
        rating,
        text: reviewText,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleShareOnGoogle = async () => {
    const reviewText = thankYou?.text || "";
    try {
      if (reviewText && navigator.clipboard) {
        await navigator.clipboard.writeText(reviewText);
        toast.success("Review copied to clipboard — paste it on Google");
      }
    } catch {
      // Clipboard permission denied — still open Google
    }
    if (googleUrl) {
      window.open(googleUrl, "_blank", "noopener,noreferrer");
    }
    setThankYou(null);
  };

  const reviewList = reviews?.reviews || [];
  const avg = reviews?.average_rating || 0;
  const count = reviews?.count || 0;

  // Show review form/button even when no reviews exist so users can leave the first review
  const hasReviews = count > 0 || reviewList.length > 0;

  return (
    <div className={hideHeader ? "" : "mt-8 pt-6 border-t border-[#282828]/8"} data-testid="review-section">
      {/* Header — hidden when used inside ReviewsTabs (parent provides its own) */}
      {!hideHeader && (
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg lg:text-xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Customer Reviews
          </h3>
          {hasReviews && count > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <StarRating value={Math.round(avg)} size="w-3.5 h-3.5" />
              <span className="text-[13px] text-[#5A5A5A]">{avg} out of 5</span>
              <span className="text-[11px] text-[#676664]/40">({count} review{count !== 1 ? 's' : ''})</span>
            </div>
          )}
        </div>
        {!showForm && !submitted && (
          <Button
            variant="outline"
            onClick={() => setShowForm(true)}
            className="text-[10px] tracking-[0.1em] uppercase border-[#282828]/12 text-[#676664] hover:border-[#9A7B4F]/30"
            data-testid="write-review-btn"
          >
            Write a Review
          </Button>
        )}
      </div>
      )}

      {/* Submission Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-[#282828]/8 p-5 mb-5" data-testid="review-form">
          <p className="text-[12px] text-[#676664]/50 tracking-[0.08em] uppercase mb-4">Share Your Experience</p>

          <div className="mb-4">
            <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Your Rating *</label>
            <StarRating value={rating} onChange={setRating} size="w-6 h-6" interactive />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Name *</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="border-[#282828]/12 text-[13px]" data-testid="review-name" />
            </div>
            <div>
              <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Email</label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Optional" className="border-[#282828]/12 text-[13px]" data-testid="review-email" />
            </div>
          </div>

          <div className="mb-4">
            <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Your Review</label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Tell us about your experience..."
              rows={3}
              className="w-full px-3 py-2.5 border border-[#282828]/12 text-[13px] text-[#282828] resize-none focus:border-[#9A7B4F] focus:outline-none bg-transparent"
              data-testid="review-text"
            />
          </div>

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={submitting} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.12em] uppercase px-6" data-testid="submit-review-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3 h-3 mr-1.5" /> Submit Review</>}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="text-[10px] text-[#676664]/50">Cancel</Button>
          </div>
        </form>
      )}

      {/* Submitted confirmation */}
      {submitted && (
        <div className="bg-[#9A7B4F]/[0.04] border border-[#9A7B4F]/15 p-4 mb-5 flex items-center gap-2.5" data-testid="review-submitted">
          <Check className="w-4 h-4 text-[#9A7B4F]" />
          <p className="text-[13px] text-[#5A5A5A]">Thank you! Your review has been submitted and is pending approval.</p>
        </div>
      )}

      {/* Review List */}
      {reviewList.length > 0 ? (
        <div data-testid="review-list">
          {reviewList.map(r => <ReviewCard key={r.id} review={r} />)}
        </div>
      ) : (
        !showForm && !submitted && hasReviews && (
          <p className="text-[13px] text-[#676664]/40 py-4">No reviews yet. Be the first to share your experience.</p>
        )
      )}

      {/* Batch 9 — Thank-you modal rendered via portal to escape any ancestor transform/overflow */}
      {thankYou && typeof document !== "undefined" && createPortal((
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => setThankYou(null)}
          data-testid="review-thankyou-modal"
        >
          <div
            className="bg-white max-w-md w-full p-6 sm:p-8 shadow-xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setThankYou(null)}
              className="absolute top-3 right-3 p-1.5 text-[#676664]/40 hover:text-[#282828]"
              data-testid="thankyou-close"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {thankYou.mode === "high" ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#9A7B4F]" strokeWidth={2} />
                  </div>
                  <h3
                    className="text-xl text-[#1A1A1A]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    Thank you for your kind words
                  </h3>
                </div>
                <p className="text-[13px] text-[#5A5A5A] leading-relaxed mb-4">
                  We're so glad you loved your Mahra experience! Would you also
                  share this review on Google? It helps others discover us.
                </p>
                <div className="bg-[#FAF8F0] border border-[#282828]/8 p-3 mb-5 text-[12px] text-[#676664] italic leading-relaxed max-h-32 overflow-y-auto">
                  "{thankYou.text || `${thankYou.rating} stars`}"
                </div>
                <p className="text-[11px] text-[#676664]/50 leading-relaxed mb-5">
                  Clicking "Share on Google" will copy your review to your
                  clipboard and open Google in a new tab — sharing there is
                  completely optional.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleShareOnGoogle}
                    disabled={!googleUrl}
                    className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.12em] uppercase flex-1"
                    data-testid="share-on-google-btn"
                  >
                    <Copy className="w-3 h-3 mr-1.5" />
                    Share on Google
                    <ExternalLink className="w-3 h-3 ml-1.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setThankYou(null)}
                    className="text-[10px] tracking-[0.12em] uppercase text-[#676664] hover:text-[#282828]"
                    data-testid="thankyou-skip"
                  >
                    No thanks
                  </Button>
                </div>
                {!googleUrl && (
                  <p className="text-[10px] text-[#676664]/40 mt-3">
                    Google review link not yet configured by the site admin.
                  </p>
                )}
              </>
            ) : (
              <>
                <h3
                  className="text-xl text-[#1A1A1A] mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  Thank you for your feedback
                </h3>
                <p className="text-[13px] text-[#5A5A5A] leading-relaxed mb-5">
                  We're sorry your experience wasn't perfect. Our team will
                  review your feedback carefully and may reach out to you
                  directly to make things right.
                </p>
                <Button
                  onClick={() => setThankYou(null)}
                  className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.12em] uppercase w-full"
                  data-testid="thankyou-close-low"
                >
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
